import React from "react";
import { Nav } from "ui-kit-ck-consultant";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCog } from "@fortawesome/free-solid-svg-icons";
import FadeIn from "react-fade-in";

import PrintClass from "../../components/general/Print";
import DashboardGrid from "../../components/home/DashboardGrid";
import DashboardCustomizer from "../../components/home/DashboardCustomizer";
import PerimeterBadge from "../../components/home/PerimeterBadge";
import { getDefaultLayout } from "../../components/home/config/dashboardPresets";
import { getAvailableWidgets } from "../../components/home/config/widgetRegistry";

import axiosConfig from "../../utils/axiosConfig";
import AuthContext from "../../context/AuthContext";

export default class Home extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      layout: [],
      isCustomizing: false,
      isLoading: true,
      error: null,
      detailData: null,
      perimeter: null,
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Dashboard error:", error, errorInfo);
    this.setState({ error: error.toString() });
  }

  static getDerivedStateFromError(error) {
    return { error: error.toString() };
  }

  static contextType = AuthContext;

  componentDidMount() {
    this.loadPreferences();
    this.loadDetailData();
    this.loadPerimeter();
  }

  loadDetailData() {
    axiosConfig("home/get-data")
      .then((res) => {
        if (res.data.success && res.data.data) {
          this.setState({ detailData: res.data.data });
        }
      })
      .catch(() => {});
  }

  loadPerimeter() {
    axiosConfig("home/get-perimeter")
      .then((res) => {
        if (res.data.success && res.data.data) {
          this.setState({ perimeter: res.data.data });
        }
      })
      .catch(() => {});
  }

  loadPreferences() {
    const { type, role } = this.context;

    axiosConfig("home/get-dashboard-preferences")
      .then((res) => {
        if (res.data.success && res.data.data) {
          const saved = res.data.data;
          const available = getAvailableWidgets(type, role);
          const availableIds = available.map((w) => w.id);

          // Keep only widgets that are still allowed for this role/type
          const validLayout = saved.filter((item) =>
            availableIds.includes(item.widgetId)
          );

          // Add any new widgets that were added since last save
          const savedIds = validLayout.map((w) => w.widgetId);
          const newWidgets = availableIds
            .filter((id) => !savedIds.includes(id))
            .map((id, idx) => ({
              widgetId: id,
              position: validLayout.length + idx,
              visible: true,
            }));

          this.setState({
            layout: [...validLayout, ...newWidgets],
            isLoading: false,
          });
        } else {
          this.setDefaultLayout();
        }
      })
      .catch(() => {
        this.setDefaultLayout();
      });
  }

  setDefaultLayout() {
    const { type, role } = this.context;
    const defaultLayout = getDefaultLayout(type, role);
    this.setState({ layout: defaultLayout, isLoading: false });
  }

  savePreferences(layout) {
    axiosConfig("home/save-dashboard-preferences", {
      data: { layout },
    });
  }

  handleReorder = (newLayout) => {
    this.setState({ layout: newLayout });
    this.savePreferences(newLayout);
  };

  handleToggleVisibility = (widgetId) => {
    const { layout } = this.state;
    const newLayout = layout.map((w) =>
      w.widgetId === widgetId ? { ...w, visible: !w.visible } : w
    );
    this.setState({ layout: newLayout });
    this.savePreferences(newLayout);
  };

  handleReset = () => {
    const { type, role } = this.context;
    const defaultLayout = getDefaultLayout(type, role);
    this.setState({ layout: defaultLayout });
    this.savePreferences(defaultLayout);
  };

  handleToggleCustomizing = () => {
    this.setState((prev) => ({ isCustomizing: !prev.isCustomizing }));
  };

  render() {
    const { layout, isCustomizing, isLoading, error, detailData, perimeter } =
      this.state;

    if (error) {
      return (
        <div style={{ padding: "40px", color: "red" }}>
          <h3>Erreur Dashboard</h3>
          <pre>{error}</pre>
        </div>
      );
    }

    if (isLoading) {
      return (
        <FadeIn>
          <Nav className="mb-10 mb-30-xl" title="Tableau de bord" />
        </FadeIn>
      );
    }

    return (
      <FadeIn>
        <Nav className="mb-10 mb-30-xl" title="Tableau de bord">
          <div
            style={{ display: "flex", alignItems: "center", gap: "12px" }}
          >
            {perimeter && <PerimeterBadge perimeter={perimeter} />}
            <span
              style={{
                cursor: "pointer",
                padding: "8px 16px",
                color: isCustomizing ? "#fff" : "#026fc2",
                backgroundColor: isCustomizing ? "#026fc2" : "#fff",
                border: isCustomizing
                  ? "1px solid #026fc2"
                  : "1px solid #026fc2",
                borderRadius: "6px",
                transition: "all 0.2s",
                fontSize: "13px",
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
              onClick={this.handleToggleCustomizing}
              title="Personnaliser le tableau de bord"
            >
              <FontAwesomeIcon icon={faCog} />
              {isCustomizing ? "Mode édition" : "Personnaliser"}
            </span>
          </div>
        </Nav>

        <div
          style={{
            transition: "margin-right 0.3s ease",
            marginRight: isCustomizing ? "340px" : "0",
          }}
        >
          <DashboardGrid
            layout={layout}
            isCustomizing={isCustomizing}
            onReorder={this.handleReorder}
            onToggleVisibility={this.handleToggleVisibility}
            detailData={detailData}
            perimeter={perimeter}
          />
        </div>

        {isCustomizing && (
          <DashboardCustomizer
            layout={layout}
            onToggleVisibility={this.handleToggleVisibility}
            onReset={this.handleReset}
            onClose={this.handleToggleCustomizing}
          />
        )}

        <PrintClass
          refs={[]}
          title={"Tableau de Bord"}
          fileName={"Tableau de Bord"}
          onPrint={() => {}}
          width={800}
        />
      </FadeIn>
    );
  }
}
